import { ContentService } from '@blk/cwp-ng-framework';
import { DZ_COLOR_NEUTRAL_400, DZ_COLOR_NEUTRAL_500 } from '@blk/daisy/core/styles';
import { AbstractRenderer, Alignment, BreakableRenderer, BusinessComponent, BusinessComponentOptions, ColumnComposition, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { InjectConfig, IretireAccumConfigKey } from '@ddt-web/config-layer';

import { CmaTableColumnComposition, TableCompositionHelper } from '../helpers/column-compositions';
import { ClientBasedCmaConfig, CmaTableConfigEntries } from './config';

export class IretireCmaTableComponent extends BusinessComponent {
	public primaryTableData: CmaTableConfigEntries[];
	public secondaryTables: Array<CmaTableConfigEntries[]>;

	@InjectConfig(IretireAccumConfigKey.CMA_TABLE_CONFIG) private readonly cmaTableConfig: ClientBasedCmaConfig = {};

	constructor(public name: string, public clientName: string, public contentService: ContentService, public visible: boolean = true) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible }));
		this.primaryTableData = this.cmaTableConfig?.[this.clientName]?.tableData;
		this.secondaryTables = this.cmaTableConfig?.[this.clientName]?.secondary;
	}

	public buildComponent(): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		if (this.primaryTableData) {
			panels.push(...this.buildCmaTablePanelItems(this.primaryTableData));
		}
		if (this.secondaryTables?.length) {
			this.secondaryTables.forEach((secondaryTableData, index) => {
				panels.push(
					new StaticParagraphComponent(`CMA_SECONDARY_TABLE_TEXT_${index}`, `CMA_SECONDARY_TABLE_TEXT_${index}`),
					...this.buildCmaTablePanelItems(secondaryTableData)
				);
			});
		}
		return panels;
	}

	private buildCmaTablePanelItems(tableData: CmaTableConfigEntries[]): Array<AbstractRenderer<SVGGElement>> {
		const rows: Array<RowPanel> = [];
		const tableDefinitions = new CmaTableColumnComposition().getColumnCompositions(
			tableData,
			[
				this.contentService.get('CMA_TABLE_HEADER_INDEX', 'Index'),
				this.contentService.get('CMA_TABLE_HEADER_RETURN', 'Annualized Assumed Return'),
				this.contentService.get('CMA_TABLE_HEADER_RISK', 'Annualized Assumed Risk')
			],
			[50, 25, 25]
		);

		const headerRow: RowPanel = TableCompositionHelper.getHeaderRowPanel('CMA Table Header Row', tableDefinitions.headers, {
			marginBottom: 5,
			marginTop: 8,
			paddingTop: 5,
			borderBottom: 0.25,
			borderBottomColor: DZ_COLOR_NEUTRAL_500
		});

		rows.push(headerRow);

		tableDefinitions.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
			const boxModelConstraint =
				index !== tableDefinitions.body.length - 1
					? { marginBottom: 5, paddingTop: 5, borderBottom: 0.25, borderBottomColor: DZ_COLOR_NEUTRAL_400 }
					: { paddingTop: 5, paddingBottom: 15 };
			rows.push(new RowPanel('bodyRow', rowRenderer, boxModelConstraint, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
		});
		return [new BreakableRenderer(rows, {}, headerRow)];
	}
}
